import { createPriceIndexApi } from '@eastcoal/api/content/priceIndexOperation/createPriceIndex'
import { queryPriceIndexApi } from '@eastcoal/api/content/priceIndexOperation/queryPriceIndex'
import { updatePriceIndexApi } from '@eastcoal/api/content/priceIndexOperation/updatePriceIndex'

import { PageContainer } from '@eastcoal/common/components/Layout/PageContainer'
import { useFeedback } from '@eastcoal/common/hooks/useFeedback'
import { useUrlParams } from '@eastcoal/common/hooks/useUrlParams'

import { CrudForm } from '@eastcoal/formily/components/CrudForm'
import { BackButton } from '@eastcoal/formily/components/CrudForm/shared/BackButton'
import { SubmitButton } from '@eastcoal/formily/components/CrudForm/shared/SubmitButton'
import { DatePicker } from '@eastcoal/formily/components/DatePicker'
import { FormItem } from '@eastcoal/formily/components/FormItem'
import { InputNumber } from '@eastcoal/formily/components/InputNumber'
import { Select } from '@eastcoal/formily/components/Select'

import { useNavigate } from '@eastcoal/router/exports/react-router'
import { KindAllNodeCascader } from '@eastcoal/shared/@formily/KindAllNodeCascader'

import { PriceIndexFormProps } from '../Category/Form/interface'
import { PriceIndexCategorySelect } from '../components/PriceIndexCategorySelect'

import { PriceIndexFormilyProps } from './interface'
import { PriceIndexPriceCalculator } from './PriceCalculator'

const schema: PriceIndexFormilyProps['schema'] = [
  {
    name: '{ id: categoryId, name: categoryName }',
    title: '{{  $values.priceIndexType === "0" ? "指数类别" : "类别" }}',
    type: 'object',
    required: true,
    'x-component': PriceIndexCategorySelect,
    'x-component-props': {
      priceIndexType: '{{ $values.priceIndexType }}'
    },
    'x-decorator': FormItem
  },
  {
    name: 'indexTime',
    title: '日期',
    type: 'string',
    required: true,
    'x-component': DatePicker,
    'x-decorator': FormItem
  },
  {
    name: '{ id: kindId, name: kindName, code: kindCode }',
    title: '品种',
    type: 'object',
    required: true,
    'x-component': KindAllNodeCascader,
    'x-decorator': FormItem
  },
  {
    name: 'fillMethod',
    title: '填写方式',
    type: 'string',
    enum: [
      { label: '手动录入', value: 0 },
      { label: '加权平均数录入', value: 1 }
    ],
    'x-display': '{{ $values.priceIndexType === "0" ? "visible" : "hidden" }}',
    'x-component': Select,
    'x-decorator': FormItem
  },
  {
    name: 'price',
    title: '{{  $values.priceIndexType === "0" ? "指数价格" : "价格" }}',
    type: 'number',
    required: true,
    'x-pattern': '{{ $values.fillMethod === 0 ? "editable" : "disabled" }}',
    'x-component': InputNumber,
    'x-decorator': FormItem
  },
  {
    name: 'priceCalculator',
    type: 'void',
    'x-display': '{{ $values.fillMethod === 1 ? "visible" : "none" }}',
    'x-component': PriceIndexPriceCalculator,
    'x-decorator': FormItem,
    'x-decorator-props': {
      fullness: true,
      wrapperWidth: 'auto'
    }
  }
]

const readApi: PriceIndexFormilyProps['readApi'] = ({ id }) => {
  return {
    api: () => queryPriceIndexApi({ id }),
    ready: !!id,
    refreshDeps: [id]
  }
}

const grid: PriceIndexFormilyProps['grid'] = { columns: 1 }

const layout: PriceIndexFormilyProps['layout'] = {
  labelWidth: '6em',
  wrapperWidth: 480
}

export const PriceIndexForm = (props: PriceIndexFormProps) => {
  const { priceIndexType } = useUrlParams()

  const navigate = useNavigate()
  const { message } = useFeedback()
  const handleSubmitSuccess: PriceIndexFormilyProps['onSuccess'] = (
    _,
    { keepon }
  ) => {
    message.success('操作成功')
    if (!keepon) {
      navigate(-1)
    }
  }

  return (
    <PageContainer>
      <CrudForm
        {...props}
        createApi={createPriceIndexApi}
        grid={grid}
        layout={layout}
        name='priceIndexForm'
        readApi={readApi}
        schema={schema}
        updateApi={updatePriceIndexApi}
        actions={[
          <BackButton key='backButton' />,
          <SubmitButton key='submitButton'>提交</SubmitButton>,
          <SubmitButton key='submitButton2' keepon>
            提交并继续
          </SubmitButton>
        ]}
        initialValues={{
          priceIndexType,
          fillMethod: 0
        }}
        onSuccess={handleSubmitSuccess}
      />
    </PageContainer>
  )
}
